import { Box } from "@itandi/itandi-bb-ui/Box";
import { useContext, useMemo } from "react";
import { useSWRConfig } from "swr";
import { ConstructionClientNavigation } from "~/components/common/constructions/navigation/ConstructionClientNavigation";
import { ReportImageNavigation } from "~/components/common/reports/navigation/ReportImageNavigation";
import { StatusHandleClientButtons } from "~/components/common/statuses/StatusHandleClientButtons";
import { UploadFileNavigation } from "~/components/common/upload_files/UploadFileNavigation";
import { ScheduleInputNavigation } from "~/components/common/visit_schedule/ScheduleInputNavigation";
import { useDealContext } from "~/context/DealContext";
import { useDealEstimationHistories } from "~/hooks/deals/useDealEstimationHistories";
import { useDealEstimations } from "~/hooks/deals/useDealEstimations";
import { useDealInspectionReportImages } from "~/hooks/deals/useDealInspectionReportImages";
import { dealCacheKey } from "~/hooks/useDeals";
import type { NskResponse } from "~/models/Api";
import { ESTIMATION_BY, matchDealType } from "~/models/Deal";
import { navigationTitle } from "~/models/DealReportImage";
import type { DealUploadFile } from "~/models/DealUploadFile";
import { ItandiAccountContext } from "~/models/ItandiAccount";
import {
	IN_ESTIMATION,
	SEND_ESTIMATE,
	statusMovingValidation
} from "~/models/Status";
import { PATH as INSPECTION_REPORT_PATH } from "~/pages/deals/[id]/inspection_reports";
import { create as uploadMemorandumFile } from "~/utils/api/deals/DealMemorandumFile";
import {
	destroy as destroyUploadFile,
	getFilePath as getUploadedFilePath
} from "~/utils/api/deals/DealUploadedFile";
import Styles from "./statuses.module.scss";

export function InEstimation() {
	const deal = useDealContext();
	const { mutate: globalMutate } = useSWRConfig();
	const account = useContext(ItandiAccountContext);
	const { data: dealEstimations } = useDealEstimations().index(deal.id);
	const { data: inspectionImages } = useDealInspectionReportImages().index(
		deal.id
	);
	const { data: dealEstimationHistories } = useDealEstimationHistories().index(
		deal.id
	);
	const forwardStatusDisabled = useMemo(() => {
		if (
			dealEstimationHistories === null ||
			dealEstimations === null ||
			inspectionImages === null
		)
			return true;

		return !statusMovingValidation[IN_ESTIMATION](
			deal,
			dealEstimations,
			dealEstimationHistories,
			inspectionImages,
			account.isAdmin,
			account
		);
	}, [
		deal,
		dealEstimations,
		dealEstimationHistories,
		inspectionImages,
		account
	]);

	if (inspectionImages === null) return null;
	const inspectionName = matchDealType(deal, "restoration")
		? "退去の立会"
		: "立会";
	const handleMutateDeal = async (): Promise<void> => {
		await globalMutate(dealCacheKey(deal.id));
	};
	const handleUploadMemorandum = async (file: File): Promise<NskResponse> =>
		uploadMemorandumFile(file, deal.id);
	const handleDestroyMemorandum = async (
		file: DealUploadFile
	): Promise<NskResponse> => destroyUploadFile(deal.id, file.id);
	const handleGetMemorandumFilePath = (file: DealUploadFile): string =>
		getUploadedFilePath(deal.id, file);
	return (
		<div>
			<h3 className={Styles.Title}>{inspectionName}を依頼しました</h3>
			<Box marginY={16}>
				<p>
					{deal.estimationBy === ESTIMATION_BY.VENDOR
						? `${inspectionName}が完了して、見積りが提出されるまでお待ち下さい。`
						: "見積りの登録をしてください。見積りの登録を完了させることで、ステータスを進めることができます。"}
				</p>
			</Box>
			{deal.inspectionBy === "company" && (
				<ScheduleInputNavigation deal={deal} handleMutate={handleMutateDeal} />
			)}
			<ReportImageNavigation
				detailPageButtonLabel="画像登録画面"
				detailPagePath={INSPECTION_REPORT_PATH(deal.id)}
				optionalLabel
				reportImages={inspectionImages}
				title={navigationTitle(deal)}
			/>
			{deal.estimationBy === ESTIMATION_BY.COMPANY && (
				<ConstructionClientNavigation title="見積りを入力してください" />
			)}
			<UploadFileNavigation
				deal={deal}
				files={deal.dealMemorandumFiles}
				fileType="賃借人同意書"
				handleDestroy={handleDestroyMemorandum}
				handleGetFilePath={handleGetMemorandumFilePath}
				handleMutate={handleMutateDeal}
				handleUpload={handleUploadMemorandum}
				title="賃借人同意書を登録してください"
			/>
			<StatusHandleClientButtons
				forwardDisabled={forwardStatusDisabled}
				nextStatusId={SEND_ESTIMATE}
			/>
		</div>
	);
}
